(() => {
  'use strict';

  if (window.HomewoodsTheme) {
    window.HomewoodsTheme.mount(document);
    return;
  }

  const instances = new Map();

  const parseJson = (element, fallback) => {
    try {
      return element ? JSON.parse(element.textContent) : fallback;
    } catch {
      return fallback;
    }
  };

  const substitute = (text, values) => Object.entries(values).reduce(
    (result, [key, value]) => result.split(`__${key}__`).join(String(value)),
    String(text || '')
  );

  class HomewoodsLanding {
    constructor(root) {
      this.root = root;
      this.listeners = [];
      this.observers = [];
      this.requests = new Set();
      this.destroyed = false;
      this.pending = false;
      this.estimate = null;
      this.form = root.querySelector('[data-hw-product-form]');
      this.select = this.form?.querySelector('[data-variant-select]');
      this.quantity = this.form?.querySelector('[data-product-quantity]');
      this.labels = parseJson(root.querySelector('[data-hw-strings]'), {});
      const variants = parseJson(root.querySelector('[data-hw-variants]'), []);
      this.variants = new Map(Array.isArray(variants) ? variants.filter((variant) => variant?.id).map((variant) => [String(variant.id), variant]) : []);
      this.productReady = Boolean(this.form && this.select && this.quantity && this.variants.size);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videoStates = new Map();
      this.gifStates = new Map();
      this.listen(root, 'click', (event) => this.onClick(event));
      this.listen(root, 'change', (event) => this.onChange(event));
      this.listen(root, 'input', (event) => this.onInput(event));
      if (this.form) this.listen(this.form, 'submit', (event) => this.onSubmit(event), true);
      this.listen(window, 'popstate', () => this.onPopState());
      this.listen(document, 'homewoods:measurements-change', () => this.updatePurchaseSummary());
      this.listen(document, 'visibilitychange', () => {
        this.updateAllVideos();
        this.updateAllGifs();
      });
      this.listen(this.reducedMotion, 'change', () => {
        if (this.reducedMotion.matches) {
          this.videoStates.forEach((state) => { state.intent = 'auto'; });
          this.gifStates.forEach((state) => { state.intent = 'paused'; });
        }
        this.updateAllVideos();
        this.updateAllGifs();
      });
      this.setupVideos();
      this.setupGifs();
      if (this.productReady) {
        this.setupProduct();
        this.setupSticky();
      }
      this.updateCalculator();
      this.root.dataset.hwReady = 'true';
    }

    listen(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      this.listeners.push(() => target.removeEventListener(name, handler, options));
    }

    setupProduct() {
      this.form.querySelectorAll('[data-qty-change]').forEach((button) => { button.hidden = false; });
      const finishes = this.form.querySelector('[data-finish-fieldset]');
      if (finishes) {
        finishes.hidden = false;
        const native = this.form.querySelector('[data-variant-native]');
        if (native) native.hidden = true;
      }
      const requested = new URL(window.location.href).searchParams.get('variant');
      const initial = requested && this.variants.has(requested) ? requested : this.select.value;
      this.setVariant(initial, { changeImage: false });
      this.setupBundles();
      this.syncControls();
    }

    setupBundles() {
      this.bundleSelector = this.form.querySelector('[data-bundle-selector]');
      this.extraQuantity = this.form.querySelector('[data-extra-quantity]');
      if (!this.bundleSelector || !this.extraQuantity) return;
      this.bundleSizes = Array.from(this.bundleSelector.querySelectorAll('[data-bundle-select]'), (button) => Number(button.dataset.bundleSelect));
      this.bundleQuantity = this.bundleSizes[0];
      this.reconcileBundleQuantity(true);
      this.listen(this.bundleSelector, 'keydown', (event) => {
        if (!(event.target instanceof Element) || !event.target.matches('[data-bundle-select]')) return;
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const buttons = Array.from(this.bundleSelector.querySelectorAll('[data-bundle-select]')).filter((button) => !button.disabled);
        if (!buttons.length) return;
        const direction = ['ArrowUp', 'ArrowLeft'].includes(event.key) ? -1 : 1;
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
          : (buttons.indexOf(event.target) + direction + buttons.length) % buttons.length;
        buttons[next].focus();
        buttons[next].click();
      });
      this.bundleSelector.hidden = false;
      this.form.querySelector('[data-extra-panels]').hidden = false;
      this.form.querySelector('[data-native-quantity]').hidden = true;
      this.form.querySelector('[data-bundle-summary]').hidden = false;
      this.form.dataset.bundlesReady = 'true';
    }

    reconcileBundleQuantity(chooseBundle = false) {
      if (!this.bundleSelector) return;
      const quantity = Number(this.quantity.value);
      if (chooseBundle || !this.bundleSizes.includes(this.bundleQuantity) || this.bundleQuantity > quantity) {
        this.bundleQuantity = this.bundleSizes.filter((size) => size <= quantity).at(-1) || this.bundleSizes[0];
      }
      this.extraCount = Math.max(0, quantity - this.bundleQuantity);
      this.extraQuantity.value = String(this.extraCount);
    }

    setBundleQuantity(bundle, extras) {
      if (this.pending || !this.available() || !this.bundleSizes?.includes(bundle)) return;
      const safeExtras = Number.isFinite(extras) ? Math.max(0, Math.min(Math.ceil(extras), Number.MAX_SAFE_INTEGER - bundle)) : 0;
      this.bundleQuantity = bundle;
      this.quantity.value = String(this.normalizedQuantity(bundle + safeExtras, true));
      this.reconcileBundleQuantity();
      this.clearMessages();
      this.syncControls();
    }

    syncBundles() {
      if (!this.bundleSelector) return;
      const { minimum, increment, maximum } = this.rules();
      const allowedMaximum = maximum === null ? Number.MAX_SAFE_INTEGER
        : minimum + Math.floor((maximum - minimum) / increment) * increment;
      const disabled = this.pending || !this.available();
      const variant = this.currentVariant();
      const price = variant.price;
      const image = this.currentOption()?.dataset.image || '';
      if (this.bundleImage !== image) {
        if (image) this.bundleSelector.style.setProperty('--hw-bundle-image', `url(${JSON.stringify(image)})`);
        else this.bundleSelector.style.removeProperty('--hw-bundle-image');
        this.bundleImage = image;
      }
      this.bundleSelector.dataset.finishId = String(variant.id);
      this.bundleSelector.querySelectorAll('[data-bundle-select]').forEach((button) => {
        const size = Number(button.dataset.bundleSelect);
        const selected = size === this.bundleQuantity;
        button.disabled = disabled || size > allowedMaximum;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
        button.querySelector('[data-bundle-price]').textContent = this.formatMoney(size * price);
      });
      const minimumExtras = Math.max(0, this.normalizedQuantity(this.bundleQuantity, true) - this.bundleQuantity);
      const maximumExtras = Math.max(0, allowedMaximum - this.bundleQuantity);
      this.extraQuantity.min = String(minimumExtras);
      this.extraQuantity.step = String(increment);
      this.extraQuantity.max = String(maximumExtras);
      this.extraQuantity.disabled = disabled;
      this.form.querySelectorAll('[data-extra-change]').forEach((button) => {
        button.disabled = disabled || (Number(button.dataset.extraChange) < 0
          ? this.extraCount <= minimumExtras : this.extraCount + increment > maximumExtras);
      });
      const summary = this.form.querySelector('[data-bundle-summary]');
      const total = Number(this.quantity.value);
      const extraLabel = this.extraCount ? ` + ${this.extraCount} ${this.extraCount === 1 ? 'extra' : 'extras'}` : '';
      summary.textContent = `${this.bundleQuantity}-panel bundle${extraLabel} · ${total} ${total === 1 ? 'panel' : 'panels'} total`;
    }

    setupSticky() {
      const bar = this.root.querySelector('[data-sticky-bar]');
      const purchase = this.root.querySelector('[data-hw-product]');
      if (!bar || !purchase || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const visible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          bar.dataset.visible = String(visible);
          bar.inert = !visible;
        });
      }, { threshold: 0 });
      bar.inert = true;
      observer.observe(purchase);
      this.observers.push(observer);
    }

    currentVariant() {
      return this.variants.get(String(this.select?.value));
    }

    currentOption() {
      return this.select ? Array.from(this.select.options).find((option) => option.value === this.select.value) : null;
    }

    available() {
      return Boolean(this.productReady && this.currentVariant()?.available);
    }

    rules() {
      const option = this.currentOption();
      const minimum = Math.max(1, Number(option?.dataset.quantityMin) || 1);
      const increment = Math.max(1, Number(option?.dataset.quantityStep) || 1);
      const suppliedMaximum = Number(option?.dataset.quantityMax);
      const maximum = option?.dataset.quantityMax && Number.isFinite(suppliedMaximum) ? suppliedMaximum : null;
      return { minimum, increment, maximum };
    }

    normalizedQuantity(value, roundUp = false) {
      const { minimum, increment, maximum } = this.rules();
      const parsed = Number(value);
      const safe = Number.isFinite(parsed) ? Math.max(minimum, parsed) : minimum;
      const steps = (safe - minimum) / increment;
      let quantity = minimum + (roundUp ? Math.ceil(steps) : Math.floor(steps)) * increment;
      if (maximum !== null) {
        const allowedMaximum = minimum + Math.floor((maximum - minimum) / increment) * increment;
        quantity = Math.min(quantity, allowedMaximum);
      }
      return Math.max(minimum, quantity);
    }

    setVariant(id, { updateHistory = false, changeImage = true } = {}) {
      const variant = this.variants.get(String(id));
      if (!variant) return;
      this.select.value = String(variant.id);
      const option = this.currentOption();
      if (!option) return;
      const isDefault = variant.title === 'Default Title';
      const finish = isDefault ? this.labels.defaultFinish : variant.title;
      this.root.querySelectorAll('[data-selected-finish], [data-sticky-finish]').forEach((element) => { element.textContent = finish; });
      this.root.querySelectorAll('[data-product-price], [data-sticky-price]').forEach((element) => { element.textContent = option.dataset.price || ''; });
      this.root.querySelectorAll('[data-compare-price]').forEach((element) => {
        element.hidden = !(Number(variant.compare_at_price) > Number(variant.price));
        element.textContent = option.dataset.comparePrice || '';
      });
      this.form.querySelectorAll('[data-finish-select]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.finishSelect === String(variant.id)));
      });
      const { minimum, increment, maximum } = this.rules();
      this.quantity.min = String(minimum);
      this.quantity.step = String(increment);
      if (maximum === null) this.quantity.removeAttribute('max');
      else this.quantity.max = String(maximum);
      this.quantity.value = String(this.normalizedQuantity(this.quantity.value, true));
      this.reconcileBundleQuantity();
      const rulesElement = this.form.querySelector('[data-quantity-rules]');
      if (rulesElement) {
        rulesElement.hidden = minimum === 1 && increment === 1 && maximum === null;
        rulesElement.textContent = substitute(this.labels.quantityRule, { minimum, increment })
          + (maximum === null ? '' : ` ${substitute(this.labels.quantityMaximum, { maximum })}`);
      }
      this.clearMessages();
      this.syncControls();
      this.updateCalculator();
      const finishGalleryReady = window.HomewoodsFinishGallery?.sync(this.root, finish, (src, alt, button) => this.showImage(src, alt, button));
      if (!finishGalleryReady && changeImage && option.dataset.image) this.showImage(option.dataset.image, option.dataset.imageAlt || finish);
      if (updateHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }

    syncControls() {
      if (!this.productReady) return;
      this.updatePurchaseSummary();
      this.syncBundles();
      const isAvailable = this.available();
      const quantity = Number(this.quantity.value);
      const readyLabel = this.bundleSelector ? `Add ${quantity} ${quantity === 1 ? 'panel' : 'panels'} to cart` : this.labels.add;
      const label = this.pending ? this.labels.adding : (isAvailable ? readyLabel : this.labels.soldOut);
      this.form.setAttribute('aria-busy', String(this.pending));
      const addButton = this.form.querySelector('[data-add-to-cart]');
      if (addButton) addButton.disabled = this.pending || !isAvailable;
      this.form.querySelectorAll('[data-add-label]').forEach((element) => { element.textContent = label; });
      this.select.disabled = this.pending;
      this.quantity.disabled = this.pending || !isAvailable;
      const { minimum, maximum, increment } = this.rules();
      this.form.querySelectorAll('[data-qty-change]').forEach((button) => {
        const decrease = Number(button.dataset.qtyChange) < 0;
        button.disabled = this.pending || !isAvailable || (decrease ? quantity <= minimum : maximum !== null && quantity + increment > maximum);
      });
      this.form.querySelectorAll('[data-finish-select]').forEach((button) => {
        button.disabled = this.pending || !this.variants.get(button.dataset.finishSelect)?.available;
      });
      this.root.querySelectorAll('[data-sticky-buy]').forEach((button) => {
        const disabled = this.pending || !isAvailable;
        button.setAttribute('aria-disabled', String(disabled));
        if (button instanceof HTMLButtonElement) button.disabled = disabled;
        const text = button.querySelector('[data-sticky-buy-label]');
        if (text) text.textContent = label;
      });
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.root.dataset.currency || window.Shopify?.currency?.active || 'USD', currencyDisplay: 'code' }).format(cents / 100);
    }

    updatePurchaseSummary() {
      const quantity = Number(this.quantity?.value);
      const valid = Number.isSafeInteger(quantity) && quantity > 0;
      const area = this.root.querySelector('[data-order-area]');
      const total = this.root.querySelector('[data-order-total]');
      if (area) area.textContent = valid ? (window.HomewoodsMeasurements?.panelArea(quantity) || `${(quantity * 2.97).toFixed(2)} m²`) : '—';
      if (total) total.textContent = valid && this.currentVariant() ? this.formatMoney(quantity * this.currentVariant().price) : '—';
      this.root.querySelectorAll('[data-sticky-price]').forEach((element) => {
        element.textContent = valid && this.currentVariant() ? this.formatMoney(quantity * this.currentVariant().price) : '—';
      });
    }

    clearMessages() {
      const error = this.root.querySelector('[data-form-error]');
      const status = this.root.querySelector('[data-form-status]');
      const confirmation = this.root.querySelector('[data-cart-confirmation]');
      if (error) { error.textContent = ''; error.hidden = true; }
      if (status) status.textContent = '';
      if (confirmation) confirmation.hidden = true;
    }

    showError(message, showCart = false) {
      const error = this.root.querySelector('[data-form-error]');
      if (error) { error.textContent = String(message || this.labels.addError); error.hidden = false; }
      if (showCart) this.showCartLink();
    }

    showCartLink() {
      const confirmation = this.root.querySelector('[data-cart-confirmation]');
      if (confirmation) confirmation.hidden = false;
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-finish-select], [data-qty-change], [data-bundle-select], [data-extra-change], [data-gallery-select], [data-video-toggle], [data-gif-toggle], [data-use-estimate], [data-sticky-buy]');
      if (!button || !this.root.contains(button)) return;
      if (button.matches('[data-finish-select]') && this.productReady) {
        if (!this.pending && !button.disabled) this.setVariant(button.dataset.finishSelect, { updateHistory: true });
      } else if (button.matches('[data-bundle-select]') && this.productReady) {
        if (!button.disabled) this.setBundleQuantity(Number(button.dataset.bundleSelect), this.extraCount);
      } else if (button.matches('[data-extra-change]') && this.productReady) {
        if (!button.disabled) this.setBundleQuantity(this.bundleQuantity, this.extraCount + Number(button.dataset.extraChange) * this.rules().increment);
      } else if (button.matches('[data-qty-change]') && this.productReady) {
        const increment = this.rules().increment;
        this.quantity.value = String(this.normalizedQuantity(Number(this.quantity.value) + Number(button.dataset.qtyChange) * increment));
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (button.matches('[data-gallery-select]')) {
        if (button.dataset.src) this.showImage(button.dataset.src, button.dataset.alt || '', button);
      } else if (button.matches('[data-video-toggle]')) {
        this.toggleVideo(button);
      } else if (button.matches('[data-gif-toggle]')) {
        this.toggleGif(button);
      } else if (button.matches('[data-use-estimate]')) {
        this.applyEstimate();
      } else if (button.matches('[data-sticky-buy]')) {
        if (!this.productReady) return;
        event.preventDefault();
        if (!this.pending && this.available()) {
          if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
          else this.form.querySelector('[data-add-to-cart]')?.click();
        } else if (!this.pending) this.focusPurchase();
      }
    }

    onChange(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.matches('[data-variant-select]') && this.productReady) {
        this.setVariant(this.select.value, { updateHistory: true });
      } else if (event.target.matches('[data-product-quantity]') && this.productReady) {
        this.quantity.value = String(this.normalizedQuantity(this.quantity.value, true));
        this.reconcileBundleQuantity(true);
        this.clearMessages();
        this.syncControls();
      } else if (event.target.matches('[data-extra-quantity]') && this.productReady) {
        this.setBundleQuantity(this.bundleQuantity, Number(this.extraQuantity.value));
      } else if (event.target.matches('[data-wall-width], [data-wall-height], [data-wall-unit], [data-cut-allowance]')) {
        this.updateCalculator();
      }
    }

    onInput(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.matches('[data-wall-width], [data-wall-height]')) this.updateCalculator();
      if (event.target.matches('[data-product-quantity]') && this.productReady) this.updatePurchaseSummary();
      if (event.target.matches('[data-extra-quantity]') && this.productReady && this.extraQuantity.value !== '' && this.extraQuantity.validity.valid) {
        this.setBundleQuantity(this.bundleQuantity, Number(this.extraQuantity.value));
      }
    }

    onPopState() {
      if (!this.productReady || this.pending) return;
      const id = new URL(window.location.href).searchParams.get('variant');
      if (id && id !== this.select.value && this.variants.has(id)) this.setVariant(id);
    }

    endpoint(path) {
      if (!path) return null;
      try {
        const url = new URL(path, window.location.href);
        if (url.origin !== window.location.origin) return null;
        if (!url.pathname.endsWith('.js')) url.pathname += '.js';
        return url.toString();
      } catch {
        return null;
      }
    }

    async refreshCart() {
      const endpoint = this.endpoint(this.form?.dataset.cartUrl || window.HomewoodsRoutes?.cart || `${window.Shopify?.routes?.root || '/'}cart`);
      if (!endpoint || this.destroyed) return;
      const controller = new AbortController();
      this.requests.add(controller);
      try {
        const response = await fetch(endpoint, { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal, cache: 'no-store' });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.destroyed || !Number.isInteger(cart.item_count)) return;
        document.querySelectorAll('[data-cart-count]').forEach((element) => {
          element.textContent = String(cart.item_count);
          element.dataset.count = String(cart.item_count);
        });
        document.dispatchEvent(new CustomEvent('homewoods:cart-updated', { detail: { cart } }));
        return cart;
      } catch {
        return;
      } finally {
        this.requests.delete(controller);
      }
    }

    async onSubmit(event) {
      if (event.target !== this.form || !this.productReady) return;
      if (this.pending || !this.available()) { event.preventDefault(); return; }
      if (typeof window.fetch !== 'function' || typeof window.FormData !== 'function' || typeof window.AbortController !== 'function') return;
      const endpoint = this.endpoint(this.form.dataset.cartAddUrl || window.HomewoodsRoutes?.cartAdd || `${window.Shopify?.routes?.root || '/'}cart/add`);
      if (!endpoint) return;
      event.preventDefault();
      this.clearMessages();
      if (!this.form.checkValidity()) { this.form.reportValidity(); return; }
      const quantity = Number(this.quantity.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) { this.showError(this.labels.invalidQuantity); return; }
      const trigger = event.submitter || document.activeElement;
      const body = new FormData(this.form);
      const controller = new AbortController();
      this.requests.add(controller);
      this.pending = true;
      document.dispatchEvent(new CustomEvent('homewoods:cart-busy', { detail: { pending: true } }));
      this.syncControls();
      this.updateCalculator();
      const status = this.root.querySelector('[data-form-status]');
      if (status) status.textContent = this.labels.adding;
      try {
        const response = await fetch(endpoint, {
          method: 'POST', body, credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: controller.signal
        });
        const data = await response.json();
        if (this.destroyed) return;
        const added = response.ok && !data.status;
        if (!added) {
          if (status) status.textContent = '';
          this.showError(typeof data.description === 'string' ? data.description : this.labels.addError, true);
        } else {
          if (status) status.textContent = this.labels.added;
          this.showCartLink();
        }
        const cart = await this.refreshCart();
        if (added && cart && !this.destroyed) {
          document.dispatchEvent(new CustomEvent('homewoods:cart-added', { detail: { cart, trigger } }));
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !this.destroyed) {
          if (status) status.textContent = '';
          this.showError(this.labels.networkError, true);
          await this.refreshCart();
        }
      } finally {
        this.requests.delete(controller);
        this.pending = false;
        document.dispatchEvent(new CustomEvent('homewoods:cart-busy', { detail: { pending: false } }));
        if (!this.destroyed) { this.syncControls(); this.updateCalculator(); }
      }
    }

    showImage(src, alt, selectedButton) {
      const gallery = this.root.querySelector('[data-hw-gallery]');
      const image = gallery?.querySelector('[data-main-image]');
      if (!image || !src) return;
      const video = gallery.querySelector('[data-gallery-video]');
      const sourceVideo = selectedButton?.dataset.videoTarget
        ? document.getElementById(selectedButton.dataset.videoTarget) : null;
      const videoSrc = sourceVideo && this.root.contains(sourceVideo)
        ? sourceVideo.querySelector('source')?.getAttribute('src') : null;
      const showVideo = Boolean(video && videoSrc);
      image.hidden = showVideo;
      image.removeAttribute('srcset');
      image.alt = String(alt || '');
      image.src = src;
      if (selectedButton?.dataset.srcset) image.srcset = selectedButton.dataset.srcset;
      if (video) {
        video.hidden = !showVideo;
        if (showVideo) {
          video.poster = src;
          video.setAttribute('aria-label', String(alt || 'Installation video'));
          if (video.getAttribute('src') !== videoSrc) video.src = videoSrc;
        }
        const state = this.videoStates.get(video);
        if (state) {
          state.intent = showVideo ? 'playing' : 'paused';
          const bounds = video.getBoundingClientRect();
          state.visible = showVideo && bounds.bottom > 0 && bounds.top < window.innerHeight;
          this.updateVideo(video);
        }
      }
      const caption = gallery.querySelector('[data-gallery-caption]');
      if (caption) caption.textContent = selectedButton?.dataset.caption || 'Finish close-up';
      const counter = gallery.querySelector('[data-gallery-counter]');
      if (counter) counter.textContent = selectedButton
        ? `${selectedButton.dataset.galleryIndex} / ${gallery.querySelectorAll('[data-gallery-select]').length}` : 'Finish';
      const watchLink = gallery.querySelector('.hw-gallery__watch');
      if (watchLink) watchLink.hidden = showVideo || selectedButton?.dataset.hideWatch === 'true';
      gallery.querySelectorAll('[data-gallery-select]').forEach((button) => {
        button.setAttribute('aria-current', String(selectedButton ? button === selectedButton : button.dataset.src === src));
      });
      const link = gallery.querySelector('[data-main-image-link]');
      if (link) link.href = src;
    }

    updateCalculator() {
      const calculator = this.root.querySelector('[data-wall-calculator]');
      if (!calculator) return;
      const width = Number(calculator.querySelector('[data-wall-width]')?.value);
      const height = Number(calculator.querySelector('[data-wall-height]')?.value);
      const unit = calculator.querySelector('[data-wall-unit]')?.value;
      const conversion = unit === 'ft' ? 0.09290304 : 1;
      const result = calculator.querySelector('[data-panel-estimate]');
      const error = calculator.querySelector('[data-calculator-error]');
      const apply = calculator.querySelector('[data-use-estimate]');
      const valid = Number.isFinite(width) && width > 0 && width <= 1000 && Number.isFinite(height) && height > 0 && height <= 1000;
      const allowance = calculator.querySelector('[data-cut-allowance]')?.checked ? 1.1 : 1;
      const wallArea = width * height * conversion;
      const areaInPanels = (wallArea * allowance) / 2.97;
      const estimate = valid ? Math.max(1, Math.ceil(areaInPanels - Number.EPSILON * Math.max(1, areaInPanels) * 8)) : null;
      this.estimate = Number.isSafeInteger(estimate) && estimate > 0 ? estimate : null;
      if (result) result.textContent = this.estimate === null ? this.labels.estimateEmpty : (this.estimate === 1 ? this.labels.estimateOne : substitute(this.labels.estimateMany, { quantity: this.estimate }));
      const formatArea = (squareMetres) => unit === 'ft' ? `${(squareMetres / conversion).toFixed(2)} sq ft` : `${squareMetres.toFixed(2)} m²`;
      const wallAreaLabel = calculator.querySelector('[data-wall-area]');
      const coverageLabel = calculator.querySelector('[data-estimate-coverage]');
      const totalLabel = calculator.querySelector('[data-estimate-total]');
      const finishLabel = calculator.querySelector('[data-estimate-finish]');
      if (wallAreaLabel) wallAreaLabel.textContent = valid ? formatArea(wallArea) : '—';
      if (coverageLabel) coverageLabel.textContent = this.estimate === null ? '—' : formatArea(this.estimate * 2.97);
      if (totalLabel) totalLabel.textContent = this.estimate === null || !this.currentVariant() ? '—' : this.formatMoney(this.estimate * this.currentVariant().price);
      if (finishLabel) finishLabel.textContent = this.currentVariant()?.option1 || '';
      const maximum = this.productReady ? this.rules().maximum : null;
      const exceedsMaximum = this.estimate !== null && maximum !== null && this.estimate > maximum;
      if (error) {
        error.hidden = !exceedsMaximum;
        error.textContent = exceedsMaximum ? substitute(this.labels.estimateLimit, { maximum }) : '';
      }
      if (apply) {
        apply.disabled = !this.available() || this.pending || this.estimate === null || exceedsMaximum;
        if (exceedsMaximum) apply.title = substitute(this.labels.estimateLimit, { maximum });
        else apply.removeAttribute('title');
      }
    }

    applyEstimate() {
      if (!this.available() || this.pending || this.estimate === null) return;
      const maximum = this.rules().maximum;
      if (maximum !== null && this.estimate > maximum) return;
      this.quantity.value = String(this.normalizedQuantity(this.estimate, true));
      this.reconcileBundleQuantity(true);
      this.clearMessages();
      const status = this.root.querySelector('[data-form-status]');
      if (status) status.textContent = substitute(this.labels.quantityUpdated, { quantity: this.quantity.value });
      this.syncControls();
      this.focusPurchase();
      this.bundleSelector?.querySelector('[data-bundle-select][aria-checked="true"]')?.focus({ preventScroll: true });
    }

    focusPurchase() {
      const target = this.root.querySelector('[data-hw-product]');
      if (!target) return;
      target.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    }

    setupVideos() {
      const videos = Array.from(this.root.querySelectorAll('[data-loop-video]'));
      videos.forEach((video) => {
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        this.videoStates.set(video, { visible: false, intent: 'auto' });
        this.listen(video, 'play', () => this.updateVideoButton(video));
        this.listen(video, 'pause', () => this.updateVideoButton(video));
        if (this.videoButton(video)) video.controls = false;
        this.updateVideoButton(video);
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = this.videoStates.get(entry.target);
            if (state) { state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.1; this.updateVideo(entry.target); }
          });
        }, { threshold: [0, 0.1] });
        videos.forEach((video) => observer.observe(video));
        this.observers.push(observer);
      } else {
        this.videoStates.forEach((state) => { state.visible = true; });
        this.updateAllVideos();
      }
    }

    videoButton(video) {
      const wrapper = video.closest('[data-video-wrap]');
      return wrapper?.querySelector('[data-video-toggle]') || null;
    }

    updateVideoButton(video) {
      const button = this.videoButton(video);
      if (!button) return;
      button.hidden = video.hidden;
      const label = video.paused ? this.labels.playVideo : this.labels.pauseVideo;
      button.setAttribute('aria-label', label || '');
      button.setAttribute('aria-pressed', String(!video.paused));
      button.dataset.state = video.paused ? 'paused' : 'playing';
      const text = button.querySelector('[data-video-toggle-label]');
      if (text) text.textContent = label || '';
    }

    updateVideo(video) {
      const state = this.videoStates.get(video);
      if (!state) return;
      const shouldPlay = !this.destroyed && !document.hidden && !video.hidden && state.visible && state.intent !== 'paused'
        && (!this.reducedMotion.matches || state.intent === 'playing');
      if (shouldPlay) {
        const promise = video.play();
        if (promise?.catch) promise.catch(() => this.updateVideoButton(video));
      } else video.pause();
      this.updateVideoButton(video);
    }

    updateAllVideos() {
      this.videoStates.forEach((state, video) => this.updateVideo(video));
    }

    toggleVideo(button) {
      const controlledId = button.getAttribute('aria-controls');
      const controlled = controlledId ? document.getElementById(controlledId) : null;
      const video = controlled && this.root.contains(controlled) ? controlled : button.closest('[data-video-wrap]')?.querySelector('[data-loop-video]');
      const state = this.videoStates.get(video);
      if (!state) return;
      state.intent = video.paused ? 'playing' : 'paused';
      if (state.intent === 'playing') state.visible = true;
      this.updateVideo(video);
    }

    setupGifs() {
      const images = Array.from(this.root.querySelectorAll('[data-motion-gif]'));
      images.forEach((image) => {
        const button = image.closest('[data-gif-wrap]')?.querySelector('[data-gif-toggle]');
        const gif = image.dataset.gifSrc;
        const poster = image.dataset.posterSrc || image.getAttribute('src');
        if (!button || !gif || !poster) return;
        this.gifStates.set(image, { button, gif, poster, visible: true, intent: 'paused', playing: false });
        button.hidden = false;
        this.updateGif(image);
      });
      if ('IntersectionObserver' in window && this.gifStates.size) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const state = this.gifStates.get(entry.target);
            if (state) { state.visible = entry.isIntersecting; this.updateGif(entry.target); }
          });
        }, { threshold: 0 });
        this.gifStates.forEach((state, image) => observer.observe(image));
        this.observers.push(observer);
      }
    }

    updateGif(image) {
      const state = this.gifStates.get(image);
      if (!state) return;
      state.playing = !this.destroyed && !document.hidden && state.visible && state.intent === 'playing';
      const src = state.playing ? state.gif : state.poster;
      if (image.getAttribute('src') !== src) image.src = src;
      const label = state.playing ? this.labels.pauseLoop : this.labels.playLoop;
      state.button.setAttribute('aria-label', label || '');
      state.button.setAttribute('aria-pressed', String(state.playing));
      state.button.dataset.state = state.playing ? 'playing' : 'paused';
      const text = state.button.querySelector('[data-gif-toggle-label]');
      if (text) text.textContent = label || '';
    }

    updateAllGifs() {
      this.gifStates.forEach((state, image) => this.updateGif(image));
    }

    toggleGif(button) {
      const image = button.closest('[data-gif-wrap]')?.querySelector('[data-motion-gif]');
      const state = this.gifStates.get(image);
      if (!state) return;
      state.intent = state.playing ? 'paused' : 'playing';
      this.updateGif(image);
    }

    destroy() {
      this.destroyed = true;
      this.listeners.forEach((remove) => remove());
      this.observers.forEach((observer) => observer.disconnect());
      this.requests.forEach((controller) => controller.abort());
      this.videoStates.forEach((state, video) => video.pause());
      this.videoStates.clear();
      this.gifStates.forEach((state, image) => {
        image.src = state.poster;
        state.button.hidden = true;
      });
      this.gifStates.clear();
      delete this.root.dataset.hwReady;
    }
  }

  const findRoots = (container) => {
    const roots = Array.from(container.querySelectorAll('[data-homewoods]'));
    if (container instanceof Element && container.matches('[data-homewoods]')) roots.unshift(container);
    return roots;
  };

  const mount = (container) => {
    findRoots(container).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new HomewoodsLanding(root));
    });
  };

  const unmount = (container) => {
    findRoots(container).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  window.HomewoodsTheme = { mount, unmount };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
})();

